/**
 * AuthProvider — single source of truth for "who am I".
 * - On mount, tries `/api/me` if a token exists; hydrates the user.
 * - Exposes login (request code + verify), logout, and the current user.
 * - Listens for the 'auth:expired' event from api.ts to auto-sign-out on 401.
 *
 * Note: this is intentionally a separate concept from useAppStore (which
 * holds game state / SRS / mistakes). Auth state lives here so the rest of
 * the app can react to login/logout without touching gameplay state.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError } from './api';

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  avatarEmoji: string;
  plan: 'free' | 'pro';
  role: 'user' | 'admin';
  createdAt: number;
  lastLoginAt: number | null;
}

interface AuthCtx {
  user: AuthUser | null;
  /** True while we're verifying the existing token on mount. */
  loading: boolean;
  /** Request a magic code. Returns the code in dev (EXPOSE_MAGIC_CODE). */
  requestCode: (email: string) => Promise<{ devCode?: string }>;
  /** Verify the code and complete sign-in. */
  verify: (email: string, code: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Hydrate on mount. With cookie auth we always try /api/me — the cookie
  // either rides or it doesn't. 401 is a silent miss, not an error.
  useEffect(() => {
    let cancelled = false;
    api<{ user: AuthUser }>('/api/me', { noAuth: true })
      .then((r) => { if (!cancelled) setUser(r.user); })
      .catch(() => { /* 401 = not signed in, fine */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Auto sign-out + redirect on 401 from any endpoint.
  useEffect(() => {
    const onExpired = (): void => {
      setUser(null);
      // Don't double-bounce if we're already on /login (e.g. failed verify).
      const path = window.location.pathname;
      if (!path.startsWith('/login')) {
        window.location.href = `/login?next=${encodeURIComponent(path)}`;
      }
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  const requestCode = useCallback(async (email: string) => {
    const r = await api<{ sent: true; code?: string }>('/api/auth/request-code', {
      method: 'POST',
      body: { email },
      noAuth: true,
    });
    return { devCode: r.code };
  }, []);

  const verify = useCallback(async (email: string, code: string) => {
    // v9: server sets HttpOnly cookie via Set-Cookie; we just get user back.
    const r = await api<{ user: AuthUser }>('/api/auth/verify', {
      method: 'POST',
      body: { email, code },
      noAuth: true,
    });
    setUser(r.user);
    return r.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } catch {
      // Ignore — server clears cookie regardless on next visit.
    }
    setUser(null);
  }, []);

  return (
    <Ctx.Provider value={{ user, loading, requestCode, verify, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (v === null) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}
